import { create } from 'zustand';
import { FormField, FieldValue } from '../types';

interface AIFillerState {
  isOpen: boolean;
  isVisible: boolean;
  hasForm: boolean;
  isProcessing: boolean;
  currentFields: FormField[];
  filledValues: FieldValue[];
  userText: string;
  formContext: string;
  error: string | null;
  suggestions: string[];
}

interface AIFillerActions {
  openPanel: () => void;
  closePanel: () => void;
  showFiller: (fields: FormField[], context?: string) => void;
  hideFiller: () => void;
  setUserText: (text: string) => void;
  setProcessing: (processing: boolean) => void;
  setFilledValues: (values: FieldValue[]) => void;
  setHasForm: (hasForm: boolean) => void;
  setError: (error: string | null) => void;
  setSuggestions: (suggestions: string[]) => void;
  reset: () => void;
}

type AIFillerStore = AIFillerState & AIFillerActions;

const initialState: AIFillerState = {
  isOpen: false,
  isVisible: false,
  hasForm: false,
  isProcessing: false,
  currentFields: [],
  filledValues: [],
  userText: '',
  formContext: '',
  error: null,
  suggestions: [],
};

export const useAIFillerStore = create<AIFillerStore>((set) => ({
  ...initialState,

  openPanel: () => set({ isOpen: true, error: null }),

  closePanel: () => set({ isOpen: false, userText: '', error: null }),

  showFiller: (fields: FormField[], context = '') =>
    set({
      isVisible: true,
      currentFields: fields,
      formContext: context,
      filledValues: [],
      error: null,
    }),

  hideFiller: () =>
    set({
      isVisible: false,
      isOpen: false,
      currentFields: [],
      filledValues: [],
      userText: '',
      error: null,
    }),

  setHasForm: (hasForm: boolean) => set({ hasForm }),

  setUserText: (text: string) => set({ userText: text }),

  setProcessing: (processing: boolean) => set({ isProcessing: processing }),

  setFilledValues: (values: FieldValue[]) => set({ filledValues: values }),

  setError: (error: string | null) => set({ error }),

  setSuggestions: (suggestions: string[]) => set({ suggestions }),

  reset: () => set(initialState),
}));

export const useAIFillerActions = () => {
  const store = useAIFillerStore();
  return {
    openPanel: store.openPanel,
    closePanel: store.closePanel,
    showFiller: store.showFiller,
    hideFiller: store.hideFiller,
    setUserText: store.setUserText,
    setProcessing: store.setProcessing,
    setFilledValues: store.setFilledValues,
    setError: store.setError,
    setSuggestions: store.setSuggestions,
    reset: store.reset,
  };
};
